import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArrowLeft01Icon,
  Download01Icon,
  DocumentValidationIcon,
  Calendar01Icon,
  CheckmarkCircle02Icon,
  ShieldEnergyIcon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { UcChristusLogo } from '@/components/brand/InstitutionalBrand';
import { PoliticaIAInfografia } from '@/components/kb/PoliticaIAInfografia';
import { KBEngagementProvider, KBEngagementBar, KBComments } from '@/components/kb/KBEngagement';

const PDF_URL = '/assets/politica_de_uso_de_herramientas_de_ia_uc_christus.pdf';

const PRINCIPIOS = [
  {
    titulo: 'Minimización',
    texto: 'Solo se usan los datos estrictamente necesarios, con base jurídica suficiente y resguardos adecuados.',
  },
  {
    titulo: 'No discriminación',
    texto: 'Se previene el uso de IA que reproduzca sesgos o genere discriminaciones arbitrarias.',
  },
  {
    titulo: 'Pertinencia y proporcionalidad',
    texto: 'La IA solo se usa ante una finalidad legítima, con relación razonable entre beneficios y riesgos.',
  },
  {
    titulo: 'Prevalencia de la dignidad',
    texto: 'La IA está al servicio del bienestar de las personas y del respeto de sus derechos.',
  },
  {
    titulo: 'Responsabilidad',
    texto:
      'Toda área y persona responsable del uso de IA debe poder justificar su utilización y controlar los resultados.',
  },
  {
    titulo: 'Seguridad del paciente y no maleficencia',
    texto: 'Ante duda sobre precisión, sesgos o pertinencia clínica, prevalece la seguridad del paciente.',
  },
  {
    titulo: 'Seguridad de la información',
    texto:
      'Toda herramienta cumple estándares de ciberseguridad, control de accesos, respaldo y respuesta a incidentes.',
  },
  {
    titulo: 'Sostenibilidad',
    texto: 'La adopción se alinea con la misión institucional, el trato digno y la humanización de la atención.',
  },
  {
    titulo: 'Supervisión humana',
    texto: 'Ninguna herramienta de IA sustituye el juicio clínico, ético, académico, profesional o administrativo.',
  },
  {
    titulo: 'Transparencia y trazabilidad',
    texto: 'El uso de IA es identificable, documentable y, cuando corresponda, explicable para su revisión y control.',
  },
];

const COMITE = [
  {
    rol: 'Gerente General',
    funcion: 'Sponsor estratégico',
    resp: 'Alineación a la estrategia corporativa y respaldo ejecutivo.',
  },
  {
    rol: 'Subdirector Médico',
    funcion: 'Validación clínica e innovación',
    resp: 'Evaluar seguridad del paciente e idoneidad clínica; conexión con innovación.',
  },
  {
    rol: 'Delegada de Protección de Datos',
    funcion: 'Privacidad y cumplimiento',
    resp: 'Normativa de protección de datos y cumplimiento regulatorio.',
  },
  {
    rol: 'Subgerente Transformación y Gobierno de Datos',
    funcion: 'Valor y datos',
    resp: 'Calidad de datos, métricas de valor y retorno.',
  },
  {
    rol: 'Gerente Sistemas e Innovación Digital',
    funcion: 'Líder del Comité',
    resp: 'Coordinación de lineamientos TI y ciclo de vida de la IA.',
  },
  {
    rol: 'Subgerente TI',
    funcion: 'Arquitectura y seguridad',
    resp: 'Infraestructura, ciberseguridad y viabilidad técnica.',
  },
];

// Secciones del documento (contenido real resumido, fiel al PDF oficial).
const SECCIONES = [
  {
    id: 'objeto',
    titulo: 'I–II. Objeto y ámbito de aplicación',
    parrafos: [
      'La Política tiene por objeto fomentar la innovación y el uso de Inteligencia Artificial en un marco de seguridad y responsabilidad institucional, regulando los principios, reglas, procedimientos y responsabilidades aplicables al uso, evaluación, adopción, desarrollo, contratación y monitoreo de sistemas y herramientas de IA en UC CHRISTUS.',
      'Su finalidad es que la adopción y uso de IA se realice en un marco de calidad, continuidad, oportunidad y humanización de la atención de salud, resguardando los derechos de las personas, la seguridad de la información, la seguridad del paciente, la confidencialidad, la protección de datos personales y sensibles, la integridad institucional y la confianza pública.',
      'Es obligatoria para directores, gerentes, jefaturas, personal clínico, académico y administrativo, estudiantes en práctica, prestadores externos, proveedores, contratistas y cualquier tercero que use, implemente, administre o contrate herramientas de IA por cuenta de UC CHRISTUS. Aplica a todo tipo de IA: generativa, analítica, predictiva, conversacional, de apoyo a decisiones, automatización documental, clasificación, transcripción, resumen, análisis de imágenes y tecnologías equivalentes.',
    ],
  },
  {
    id: 'marco',
    titulo: 'III. Marco normativo y documentos de referencia',
    parrafos: [
      'La interpretación y aplicación de la Política se realiza conforme al ordenamiento jurídico chileno, especialmente:',
    ],
    lista: [
      'Constitución Política de la República de Chile.',
      'Ley N° 20.584, sobre derechos y deberes de las personas en su atención de salud.',
      'Ley N° 19.628 y Ley N° 21.719, sobre protección de datos personales.',
      'Ley N° 21.663, Marco de Ciberseguridad.',
      'Normativa sobre ficha clínica, secreto profesional, seguridad de la información, responsabilidad sanitaria, bioética, docencia e investigación clínica.',
    ],
    cierre:
      'Actúan como referentes de buenas prácticas: la Política Nacional de IA de Chile, la Recomendación de la UNESCO sobre la Ética de la IA, la guía de la OMS sobre ética y gobernanza de IA para la salud, la Política Nacional de Ciberseguridad 2023–2028 y las políticas institucionales de seguridad de la información y gobernanza de datos.',
  },
  {
    id: 'validacion',
    titulo: 'IX. Validación, autorización y monitoreo',
    lista: [
      'Todas las herramientas pueden someterse a reevaluaciones o monitoreo periódico de su desempeño, seguridad, sesgos, trazabilidad, utilidad real e impacto, especialmente al cambiar el modelo, proveedor, caso de uso, datos o criticidad del proceso.',
      'Toda persona usuaria debe reportar de inmediato errores relevantes, resultados anómalos, brechas, sesgos, incidentes de ciberseguridad o cualquier evento que comprometa a pacientes, datos o procesos críticos.',
      'Frente a incidentes relevantes, UC CHRISTUS puede suspender, limitar o retirar la herramienta hasta contar con validación suficiente.',
      'La institución promueve indicadores cualitativos y cuantitativos y reportes periódicos de mejora continua.',
    ],
  },
  {
    id: 'datos',
    titulo: 'X. Protección de datos, confidencialidad y seguridad',
    parrafos: [
      'Todo tratamiento de datos personales mediante IA debe respetar los principios de licitud, finalidad, proporcionalidad, calidad, transparencia, seguridad, confidencialidad y responsabilidad, y los lineamientos internos de ciberseguridad, seguridad de la información y gobernanza de datos.',
      'La adopción de herramientas de proveedores externos debe contar con contrato de servicios que considere, al menos:',
    ],
    lista: [
      'Términos de servicio y condiciones de uso de datos.',
      'Ubicación, transferencia y almacenamiento de datos; segregación de la información institucional.',
      'Auditoría, logs y trazabilidad.',
      'Obligaciones del proveedor de notificación, cooperación, contención y remediación de incidentes.',
      'Continuidad operativa, respaldo, confidencialidad y restricciones de entrenamiento con datos institucionales.',
    ],
    cierre:
      'No pueden contratarse herramientas o proveedores que no ofrezcan garantías mínimas compatibles con los estándares institucionales y normativos aplicables.',
  },
  {
    id: 'propiedad',
    titulo: 'XI. Propiedad intelectual, autoría y uso de contenidos',
    lista: [
      'El uso de IA no altera las reglas sobre titularidad, confidencialidad, secreto profesional, derechos de autor, propiedad industrial y uso legítimo de bases de datos.',
      'Todo resultado generado con apoyo de IA usado en documentos, publicaciones o productos institucionales debe ser revisado por una persona responsable, que asume la responsabilidad por su contenido final.',
      'Cuando corresponda por razones académicas, científicas, éticas o de transparencia, debe declararse el uso de IA.',
    ],
  },
  {
    id: 'deberes',
    titulo: 'XII. Deberes de los usuarios',
    lista: [
      'Conocer y cumplir esta Política y los lineamientos complementarios aplicables.',
      'Usar exclusivamente herramientas autorizadas para fines institucionales.',
      'Verificar críticamente los resultados antes de emplearlos y no confiar ciegamente en respuestas automatizadas.',
      'Proteger la confidencialidad y seguridad de la información.',
      'Declarar el uso de IA cuando corresponda.',
      'Reportar incidentes, errores, sesgos, brechas o usos indebidos.',
      'Colaborar en auditorías, capacitaciones y revisiones institucionales.',
    ],
  },
  {
    id: 'incumplimientos',
    titulo: 'XIII–XVI. Docencia, incumplimientos, vigencia y publicidad',
    parrafos: [
      'En docencia e investigación clínica, la Política aplica cuando el uso de IA involucre pacientes, recursos institucionales o datos de salud cuyo responsable de tratamiento sea UC CHRISTUS; debe declararse expresamente cuando corresponda.',
      'El incumplimiento puede dar lugar a medidas disciplinarias conforme al Reglamento Interno de Orden, Higiene y Seguridad, restricciones de acceso, suspensión de herramientas, acciones contractuales o legales, según la gravedad.',
      'La Política entra en vigencia desde su aprobación formal, se revisa periódicamente ante cambios legales, tecnológicos u organizacionales, y debe estar disponible para el personal y, en lo pertinente, para pacientes y terceros.',
    ],
  },
];

const ANEXOS = [
  {
    id: 'anexo-1',
    titulo: 'Anexo N° 1 — Registro Institucional de Herramientas de IA',
    parrafos: [
      'Regula la elaboración, mantención y actualización del Registro Institucional, inventario oficial que identifica, clasifica, controla y monitorea las herramientas y casos de uso de IA. Ninguna área puede utilizar herramientas que no estén autorizadas e incluidas en el Registro.',
      'Cada herramienta y caso de uso recibe una decisión del Comité con uno de estos estados:',
    ],
    tabla: {
      headers: ['Estado', 'Efecto'],
      rows: [
        ['En evaluación', 'No puede usarse institucionalmente, salvo piloto autorizado por el Comité.'],
        ['Autorizado', 'Puede utilizarse conforme a las condiciones aprobadas.'],
        ['Autorizado con restricciones', 'Solo bajo condiciones, usuarios, datos, áreas o finalidades definidas.'],
        ['Suspendido', 'Su uso queda temporalmente detenido hasta nueva decisión.'],
        ['Retirado', 'Se deja sin efecto una autorización previa.'],
        ['Prohibido', 'No puede utilizarse institucionalmente.'],
      ],
    },
  },
  {
    id: 'anexo-2',
    titulo: 'Anexo N° 2 — Respuesta a incidentes asociados al uso de IA',
    parrafos: [
      'Establece los procedimientos para detección, notificación, contención, investigación y cierre de incidentes de IA, garantizando una respuesta oportuna, proporcional y documentada que proteja la seguridad del paciente, la privacidad de los datos y la continuidad operacional. Los incidentes se clasifican por severidad:',
    ],
    tabla: {
      headers: ['Nivel', 'Notificación', 'Contención'],
      rows: [
        ['Crítico', 'Inmediata (< 1 h)', '< 4 h'],
        ['Mayor', '< 4 h', '< 24 h'],
        ['Menor', '< 24 h', '< 72 h'],
      ],
    },
    cierre:
      'La respuesta articula a las áreas clínica, tecnológica, de seguridad de la información, protección de datos, legal y calidad. Rige el principio de no represalia: la notificación de buena fe no genera sanciones para el notificante.',
  },
  {
    id: 'anexo-3',
    titulo: 'Anexo N° 3 — Evaluación de Impacto de usos de riesgo medio',
    parrafos: ['La Evaluación de Impacto de un uso de riesgo medio debe documentar, al menos:'],
    lista: [
      'Responsables: unidad solicitante, responsable funcional y responsable técnico.',
      'Finalidad y necesidad del uso, proceso afectado y beneficios esperados.',
      'Datos a tratar: tipo, origen, calidad y ubicación; base jurídica del tratamiento.',
      'Riesgos: sesgo, discriminación, errores, alucinaciones, opacidad y dependencia excesiva.',
      'Seguridad de la información y ciberseguridad; continuidad y dependencia del proveedor.',
      'Supervisión humana significativa: responsable y etapas de intervención.',
      'Medidas de mitigación, restricciones, umbrales, plan de contingencia y criterios de suspensión.',
      'Requerimientos de validación clínica/técnica y necesidad de informar a pacientes o terceros.',
    ],
    cierre:
      'Concluida la evaluación, el Comité puede resolver: uso autorizado; autorizado con restricciones; sujeto a piloto o validación previa; suspendido hasta implementar mitigaciones; o prohibido por constituir riesgo inaceptable.',
  },
];

export default function PoliticaIA() {
  return (
    <KBEngagementProvider articleId="politica-ia">
      <div className="mx-auto max-w-5xl animate-in-up px-4 pb-16 pt-6 sm:px-6">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Button asChild variant="ghost" size="sm" className="gap-1 text-muted-foreground hover:text-foreground">
            <Link to="/kb">
              <HugeiconsIcon icon={ArrowLeft01Icon} className="h-4 w-4" aria-hidden="true" /> Knowledge Hub
            </Link>
          </Button>
        </div>

        {/* Barra de acciones sticky (guardar / like / comentar) */}
        <KBEngagementBar className="mb-6" />

        {/* Hero */}
        <header className="overflow-hidden rounded-3xl bg-gradient-hero px-6 py-10 sm:px-10 sm:py-12">
          <div className="mb-4 flex items-center gap-2">
            <UcChristusLogo contrast="light" className="h-5 opacity-90" priority />
            <span className="h-5 w-px bg-white/20" aria-hidden="true" />
            <span className="text-sm font-medium text-purple-100">Gobernanza de IA</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-white/25 bg-white/10 text-white">
              Documento oficial vigente
            </Badge>
            <span className="inline-flex items-center gap-1.5 text-xs text-white/70">
              <HugeiconsIcon icon={Calendar01Icon} className="h-3.5 w-3.5" aria-hidden="true" />
              Aprobada el 14 de julio de 2026
            </span>
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight text-white sm:text-4xl">
            Política de Uso Ético, Responsable y Seguro de Herramientas de Inteligencia Artificial
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70">
            Marco institucional que fomenta la innovación y el uso de IA en UC CHRISTUS bajo seguridad y
            responsabilidad, resguardando la dignidad de las personas, la seguridad del paciente y la protección de la
            información.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild size="lg" className="gap-2 bg-white text-primary hover:bg-white/90">
              <a href={PDF_URL} download aria-label="Descargar la política en formato PDF">
                <HugeiconsIcon icon={Download01Icon} className="h-4 w-4" aria-hidden="true" /> Descargar PDF
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="gap-2 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">
              <a href={PDF_URL} target="_blank" rel="noopener noreferrer">
                <HugeiconsIcon icon={DocumentValidationIcon} className="h-4 w-4" aria-hidden="true" /> Ver PDF original
              </a>
            </Button>
          </div>
        </header>

        {/* Resumen ejecutivo */}
        <section className="mt-10" aria-labelledby="resumen">
          <h2 id="resumen" className="font-display text-2xl font-extrabold text-foreground">
            Resumen ejecutivo
          </h2>
          <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-muted-foreground">
            <p>
              La Política se basa en <strong className="text-foreground">dos niveles de control</strong>: la herramienta
              y el uso. UC CHRISTUS evalúa cada herramienta desde una perspectiva técnica y de seguridad; una
              herramienta autorizada no implica que pueda usarse para cualquier actividad.
            </p>
            <p>
              El segundo nivel evalúa el <strong className="text-foreground">uso específico</strong> que se pretende
              realizar, clasificándolo en riesgo bajo, medio o inaceptable según el impacto sobre pacientes y terceros,
              los datos personales involucrados y los procesos críticos. Los usos de riesgo inaceptable quedan
              prohibidos en cualquier herramienta de IA.
            </p>
          </div>
        </section>

        {/* Infografía interactiva */}
        <section className="mt-10" aria-labelledby="infografia">
          <h2 id="infografia" className="sr-only">
            Infografía: cómo opera la Política
          </h2>
          <PoliticaIAInfografia />
        </section>

        {/* Clasificación de usos — detalle */}
        <section className="mt-12" aria-labelledby="clasificacion">
          <h2 id="clasificacion" className="font-display text-2xl font-extrabold text-foreground">
            VIII. Clasificación de usos de IA
          </h2>
          <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
            La clasificación distingue tres tipos de uso según su nivel de riesgo, en atención al impacto sobre las
            personas, la sensibilidad de los datos y la criticidad del contexto asistencial.
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <UsoCard
              titulo="Riesgo bajo"
              accent="secondary"
              intro="Uso instrumental, como herramienta de productividad y no como sistema decisor."
              items={[
                'Tareas administrativas sin decisiones automatizadas relevantes sobre personas.',
                'Borradores no clínicos, minutas y resúmenes internos no decisorios.',
                'Análisis descriptivos de gestión, agendas, flujos o indicadores.',
                'Usos que no requieren datos personales o que están debidamente anonimizados.',
              ]}
            />
            <UsoCard
              titulo="Riesgo medio"
              accent="primary"
              intro="Uso con criterio decisor que puede influir directa o indirectamente en las personas."
              items={[
                'Apoyo clínico en decisiones diagnósticas, pronósticas o terapéuticas.',
                'Análisis de imágenes, exámenes, registros clínicos o datos biomédicos.',
                'Priorización de pacientes, listas de espera y asignación de recursos.',
                'Chatbots o asistentes con pacientes; usos con datos personales sensibles.',
              ]}
              nota="Requiere Evaluación de Impacto y autorización previa del Comité."
            />
            <UsoCard
              titulo="Riesgo inaceptable"
              accent="destructive"
              intro="Uso que afecta derechos fundamentales, seguridad del paciente o continuidad operacional."
              items={[
                'Decisiones clínicas con efectos significativos delegadas en una herramienta de IA.',
                'Negar, limitar o retrasar prestaciones de salud.',
                'Vigilancia desproporcionada, perfilamiento indebido o trato indigno.',
                'Alterar registros clínicos o usar datos de la atención sin autorización.',
              ]}
              nota="Prohibido, aun cuando la herramienta esté autorizada."
            />
          </div>
        </section>

        {/* Principios rectores */}
        <section className="mt-12" aria-labelledby="principios">
          <div className="flex items-center gap-2.5">
            <HugeiconsIcon icon={ShieldEnergyIcon} className="h-6 w-6 text-primary" aria-hidden="true" />
            <h2 id="principios" className="font-display text-2xl font-extrabold text-foreground">
              V. Principios rectores
            </h2>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {PRINCIPIOS.map((p) => (
              <div key={p.titulo} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-start gap-3">
                  <HugeiconsIcon
                    icon={CheckmarkCircle02Icon}
                    className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                    aria-hidden="true"
                  />
                  <div>
                    <h3 className="font-semibold text-foreground">{p.titulo}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{p.texto}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Gobernanza y Comité */}
        <section className="mt-12" aria-labelledby="gobernanza">
          <div className="flex items-center gap-2.5">
            <HugeiconsIcon icon={UserGroupIcon} className="h-6 w-6 text-primary" aria-hidden="true" />
            <h2 id="gobernanza" className="font-display text-2xl font-extrabold text-foreground">
              VI–VII. Gobernanza y Comité de IA
            </h2>
          </div>
          <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
            El Comité de Gobernanza de Inteligencia Artificial es el órgano colegiado que vela por la correcta
            aplicación de la Política: revisa solicitudes de adopción, mantiene el Registro Institucional, emite
            lineamientos técnicos y éticos, revisa incidentes y promueve indicadores y reportes periódicos.
          </p>
          <div className="mt-6 overflow-hidden rounded-2xl border border-border">
            <table className="w-full border-collapse text-left text-sm">
              <caption className="sr-only">Composición del Comité de Gobernanza de IA</caption>
              <thead>
                <tr className="bg-muted/50 text-foreground">
                  <th scope="col" className="px-4 py-3 font-semibold">
                    Rol
                  </th>
                  <th scope="col" className="px-4 py-3 font-semibold">
                    Función
                  </th>
                  <th scope="col" className="hidden px-4 py-3 font-semibold sm:table-cell">
                    Responsabilidades
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMITE.map((c, i) => (
                  <tr key={c.rol} className={i % 2 ? 'bg-card' : 'bg-muted/20'}>
                    <td className="px-4 py-3 font-medium text-foreground">{c.rol}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.funcion}</td>
                    <td className="hidden px-4 py-3 text-muted-foreground sm:table-cell">{c.resp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Documento completo (secciones colapsables) */}
        <section className="mt-12" aria-labelledby="documento">
          <h2 id="documento" className="font-display text-2xl font-extrabold text-foreground">
            Documento completo
          </h2>
          <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
            Secciones de la política y sus anexos. Para el texto íntegro, descarga el PDF oficial.
          </p>
          <Accordion type="multiple" className="mt-4 rounded-2xl border border-border bg-card px-5">
            {[...SECCIONES, ...ANEXOS].map((s) => (
              <AccordionItem key={s.id} value={s.id} className="border-border last:border-b-0">
                <AccordionTrigger className="text-base font-semibold text-foreground no-underline hover:no-underline">
                  {s.titulo}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <SeccionContenido seccion={s} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* CTA descarga */}
        <div className="mt-10 flex flex-col items-center gap-4 rounded-2xl border border-border bg-muted/30 p-6 text-center">
          <HugeiconsIcon icon={DocumentValidationIcon} className="h-8 w-8 text-primary" aria-hidden="true" />
          <p className="max-w-md text-sm text-muted-foreground">
            Esta es una versión navegable del documento. Descarga la política oficial completa con todos sus anexos en
            PDF.
          </p>
          <Button asChild className="gap-2">
            <a href={PDF_URL} download aria-label="Descargar la política en formato PDF">
              <HugeiconsIcon icon={Download01Icon} className="h-4 w-4" aria-hidden="true" /> Descargar PDF oficial
            </a>
          </Button>
        </div>

        {/* Comentarios al final de la página (estilo hilo social) */}
        <div className="mt-14 border-t border-border pt-10">
          <KBComments />
        </div>
      </div>
    </KBEngagementProvider>
  );
}

function UsoCard({ titulo, accent, intro, items, nota }) {
  const border =
    accent === 'destructive'
      ? 'border-destructive/30'
      : accent === 'primary'
        ? 'border-primary/25'
        : 'border-secondary/25';
  const title =
    accent === 'destructive' ? 'text-destructive' : accent === 'primary' ? 'text-primary' : 'text-foreground';
  const notaClass = accent === 'destructive' ? 'text-destructive' : 'text-primary';
  return (
    <div className={`flex h-full flex-col rounded-2xl border ${border} bg-card p-5`}>
      <h3 className={`font-display text-lg font-bold ${title}`}>{titulo}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{intro}</p>
      <ul className="mt-3 flex-1 space-y-2">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm leading-relaxed text-foreground/80">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50" aria-hidden="true" />
            {it}
          </li>
        ))}
      </ul>
      {nota && <p className={`mt-4 border-t border-border/60 pt-3 text-sm font-semibold ${notaClass}`}>{nota}</p>}
    </div>
  );
}

function SeccionContenido({ seccion }) {
  return (
    <div className="space-y-3 text-sm leading-relaxed">
      {seccion.parrafos?.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
      {seccion.lista && (
        <ul className="space-y-1.5 pl-1">
          {seccion.lista.map((it, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/50" aria-hidden="true" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
      {seccion.tabla && (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-muted/50 text-foreground">
                {seccion.tabla.headers.map((h) => (
                  <th key={h} scope="col" className="px-3 py-2 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {seccion.tabla.rows.map((row, i) => (
                <tr key={i} className={i % 2 ? 'bg-card' : 'bg-muted/20'}>
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className={j === 0 ? 'px-3 py-2 font-medium text-foreground' : 'px-3 py-2 text-muted-foreground'}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {seccion.cierre && <p className="text-foreground/80">{seccion.cierre}</p>}
    </div>
  );
}
